import { DefaultOptions } from '@tanstack/react-query';

/**
 * React Query Default Configuration
 *
 * These settings apply to all queries and mutations globally unless overridden.
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Refetch on window focus (useful for keeping data fresh)
    refetchOnWindowFocus: false,

    // Retry failed requests
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.statusCode >= 400 && error?.statusCode < 500) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },

    // How long until data is considered stale
    staleTime: 1000 * 60 * 5, // 5 minutes

    // How long to keep unused data in cache
    gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)

    // Network mode
    networkMode: 'online',
  },
  mutations: {
    // Retry mutations once on network error
    retry: (failureCount, error: any) => {
      if (error?.statusCode >= 400 && error?.statusCode < 500) {
        return false;
      }
      return failureCount < 1;
    },

    // Network mode
    networkMode: 'online',
  },
};

/**
 * Query Keys Factory
 *
 * Centralized query key generation for consistent cache management
 */
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    twoFactorStatus: () => [...queryKeys.auth.all, 'two-factor-status'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (page: number, pageSize: number, filters?: any) =>
      [...queryKeys.users.lists(), { page, pageSize, ...filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    stats: () => [...queryKeys.users.all, 'stats'] as const,
  },

  // Career Profile
  profile: {
    all: ['profile'] as const,
    detail: () => [...queryKeys.profile.all, 'detail'] as const,
    completion: () => [...queryKeys.profile.all, 'completion'] as const,
  },
} as const;

export { queryConfig };
